import { Head } from 'vite-react-ssg';
import { useLocation } from 'react-router-dom';

type SeoProps = {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  robots?: string;
  jsonLd?: Record<string, unknown>;
  type?: 'website' | 'article' | 'profile';
};

const DEFAULT_SITE_NAME = 'Equna';
const DEFAULT_OG_IMAGE = '/ausgabenliste-detail.webp';

export function SEO({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  robots = 'index,follow',
  jsonLd,
  type = 'website'
}: SeoProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://equna.eu'; 
  
  // Use explicit canonical or fall back to current path
  const currentPath = location.pathname;
  const canonicalPath = canonical ?? currentPath;
  const canonicalUrl = new URL(canonicalPath, siteUrl).toString();
  
  // Construct absolute URL for OG Image
  const ogImageUrl = ogImage.startsWith('http') 
    ? ogImage 
    : new URL(ogImage, siteUrl).toString();

  return (
    <Head>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={DEFAULT_SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImageUrl} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Head>
  );
}
